import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MessageSquare, ArrowUp, ArrowDown, MessageCircle, Plus, Pin, TrendingUp, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';

const ForumPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const { user } = useAuth();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const categories = ['BTC', 'Altcoins', 'Stocks', 'Futures', 'Long-Term Investing', 'Entrepreneurship', 'Economics'];

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, sortBy]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy };
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await axios.get(`${API}/forum/posts`, {
        params,
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="forum-title">
                Community <span className="text-[#C5A059]">Forum</span>
              </h1>
              <p className="text-gray-300 text-lg">
                Share insights, ask questions, and connect with fellow investors.
              </p>
            </div>
            <Link to="/forum/create" className="mt-4 md:mt-0">
              <Button
                className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-3 font-semibold shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
                data-testid="create-post-button"
              >
                <Plus size={20} className="mr-2" />
                Create Post
              </Button>
            </Link>
          </div>

          {/* Search & Filters */}
          <div className="mb-8">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search discussions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] rounded-sm h-12"
                data-testid="search-input"
              />
            </div>

            {/* Sort & Category Filters */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setSortBy('recent')}
                  variant={sortBy === 'recent' ? 'default' : 'outline'}
                  className={sortBy === 'recent' ? 'bg-[#C5A059] text-black hover:bg-[#D4AF37]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm"
                  data-testid="sort-recent"
                >
                  <Clock size={16} className="mr-1" />
                  Recent
                </Button>
                <Button
                  onClick={() => setSortBy('trending')}
                  variant={sortBy === 'trending' ? 'default' : 'outline'}
                  className={sortBy === 'trending' ? 'bg-[#C5A059] text-black hover:bg-[#D4AF37]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm"
                  data-testid="sort-trending"
                >
                  <TrendingUp size={16} className="mr-1" />
                  Trending
                </Button>
                <Button
                  onClick={() => setSortBy('top')}
                  variant={sortBy === 'top' ? 'default' : 'outline'}
                  className={sortBy === 'top' ? 'bg-[#C5A059] text-black hover:bg-[#D4AF37]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm"
                  data-testid="sort-top"
                >
                  <ArrowUp size={16} className="mr-1" />
                  Top
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setSelectedCategory(null)}
                  variant={selectedCategory === null ? 'default' : 'outline'}
                  className={selectedCategory === null ? 'bg-[#007AFF] text-white hover:bg-[#2E90FA]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                  size="sm"
                  data-testid="category-all"
                >
                  All
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    className={selectedCategory === category ? 'bg-[#007AFF] text-white hover:bg-[#2E90FA]' : 'border-white/20 text-gray-300 hover:bg-white/5'}
                    size="sm"
                    data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Posts List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 text-lg mb-4" data-testid="no-posts-message">
                No posts yet. Be the first to start a discussion!
              </p>
              <Link to="/forum/create">
                <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
                  Create First Post
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPosts.map((post) => (
                <Link
                  key={post.post_id}
                  to={`/forum/${post.post_id}`}
                  className="block bg-[#111111] border border-white/10 rounded-lg p-6 hover:border-[#C5A059]/50 hover:shadow-lg transition-all duration-300 group"
                  data-testid={`post-${post.post_id}`}
                >
                  <div className="flex gap-4">
                    {/* Avatar */}
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      <AvatarImage src={post.author_picture} alt={post.author_name} />
                      <AvatarFallback className="bg-[#C5A059] text-black font-semibold">
                        {post.author_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link 
                            to={`/profile/${post.author_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-medium text-white text-sm hover:text-[#C5A059] transition-colors"
                          >
                            {post.author_name}
                          </Link>
                          <span className="text-gray-500 text-xs">•</span>
                          <span className="text-gray-500 text-xs">
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                          <span className="bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30 text-xs px-2 py-1 rounded-full">
                            {post.category}
                          </span>
                          {post.pinned && (
                            <span className="bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/30 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <Pin size={12} />
                              Pinned
                            </span>
                          )}
                        </div>
                      </div>

                      <h3 className="font-manrope font-semibold text-white text-lg mb-2 group-hover:text-[#C5A059] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {post.content}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <ArrowUp size={16} className={post.votes > 0 ? 'text-[#C5A059]' : ''} />
                          <span className={post.votes > 0 ? 'text-[#C5A059] font-medium' : ''}>{post.votes}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle size={16} />
                          <span>{post.comment_count} comments</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForumPage;
